import classNames from "classnames";
import React from "react";
import { accountingNumberFormatter } from "@common/helpers/number";
import { pluralize } from "@common/helpers/string";
import { Loader } from "@design-system/Loader/Loader";
import { Pagination } from "@design-system/Pagination/Pagination";
import { Separator } from "@design-system/Separator/Separator";
import { AccrualMatchTable } from "./AccrualMatchTable";
import { AccrualMatchSearchFooterState, MatchableItem } from "./types";
import { useMatchesPanelResize } from "./useMatchesPanelResize";
import styles from "./MatchesPanel.module.scss";

interface Props {
    title: string;
    sourceLabel: string;
    sourceItem: MatchableItem;
    matchesLabel: string;
    selectedItems: MatchableItem[];
    onUnmatch: (item: MatchableItem) => void;
    difference: number | null;
    blockedReason?: string | null;
    searchFooterState: AccrualMatchSearchFooterState | null;
    actions: React.ReactNode;
    onMatchesPanelExtraHeightChange: (px: number) => void;
    children?: React.ReactNode;
}

export const MatchesPanel: React.FC<Props> = ({
    title,
    sourceLabel,
    sourceItem,
    matchesLabel,
    selectedItems,
    onUnmatch,
    difference,
    blockedReason,
    searchFooterState,
    actions,
    onMatchesPanelExtraHeightChange,
    children,
}) => {
    const { matchesPanelRef, resizeHandleProps } = useMatchesPanelResize(
        onMatchesPanelExtraHeightChange,
    );
    const isDifferenceZero = difference === 0;

    return (
        <div className={styles.matchesPanel} ref={matchesPanelRef}>
            <div
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize matches panel"
                className={styles.resizeHandle}
                {...resizeHandleProps}
            >
                <div className={styles.resizeHandleGrip} />
            </div>
            <div className={styles.panelHeaderRow}>
                <span className={styles.panelHeaderTitle}>{title}</span>
            </div>
            <Separator />

            <div className={styles.subHeader}>{sourceLabel}</div>
            <div className={styles.sourceArea}>
                <AccrualMatchTable
                    data={[sourceItem]}
                    section="source"
                    hideHeader
                    transparentRows
                />
            </div>

            <div className={styles.subHeader}>{matchesLabel}</div>
            <div className={styles.matchesArea}>
                {selectedItems.length === 0 ? (
                    <div className={styles.placeholder}>Select matches</div>
                ) : (
                    <AccrualMatchTable
                        data={selectedItems}
                        section="selected"
                        onAction={onUnmatch}
                        hideHeader
                        transparentRows
                    />
                )}
            </div>
            {selectedItems.length === 0 && <Separator />}

            {difference !== null && (
                <div className={styles.differenceRow}>
                    <span
                        className={classNames(styles.differenceText, {
                            [styles.differenceTextActive]: !isDifferenceZero,
                        })}
                    >
                        Difference
                    </span>
                    <span
                        className={classNames(styles.differenceText, {
                            [styles.differenceTextActive]: !isDifferenceZero,
                        })}
                    >
                        {accountingNumberFormatter(difference, "$0.00")}
                    </span>
                </div>
            )}
            {blockedReason && (
                <div className={styles.blockedReasonRow}>{blockedReason}</div>
            )}
            <Separator />

            <div className={styles.footer}>
                {searchFooterState && (
                    <div className={styles.searchFooterControls}>
                        <div>
                            {searchFooterState.totalCount}{" "}
                            {pluralize("result", searchFooterState.totalCount)}
                        </div>
                        {searchFooterState.isLoading ? (
                            <Loader inline delayed />
                        ) : (
                            searchFooterState.shouldShowPagination && (
                                <Pagination
                                    pageCount={searchFooterState.pageCount}
                                    currentPage={searchFooterState.page}
                                    onPageChange={
                                        searchFooterState.onPageChange
                                    }
                                />
                            )
                        )}
                    </div>
                )}
                <div className={styles.actionFooterControls}>{actions}</div>
            </div>
            {children}
        </div>
    );
};
